/**
 * CBoxView React component
 * 
 * @author    Maurizio Tidei, Jens Gruschel
 * @copyright © 2019 contexagon GmbH
 */


// React
import React from 'react';
import {connect} from 'react-redux';

// Material-UI
import {withStyles} from "@material-ui/core";

// 3rd party
import { DndProvider } from 'react-dnd'
import MultiBackend, { Preview } from 'react-dnd-multi-backend';
import HTML5toTouch from 'react-dnd-multi-backend/dist/esm/HTML5toTouch'; // or any other pipeline

// application
import ContentView from "./ContentView";
import ChatView from "./ChatView";
import MenuFrame from "./MenuFrame";
import NotificationBar from "./NotificationBar"
import MediaPanel from "../base/MediaPanel";
import {
    CONTENT_MODE, CHAT_MODE
} from "../../redux/cbox/consts";
import {selectTranslation, selectBaseStylesheet} from "../../redux/configuration/selectors";
import {GlobalHotKeys} from "react-hotkeys";
import _ from "lodash";
import {triggerShortcutAction, refreshBox} from "../../operations/cbox";
import {DraggableMenuObjectPreview} from "./DraggableMenuObject";


function qunatifyDuration(duration) {
    switch (duration) {
        case "veryfast": return 0.125;
        case "fast": return 0.25;
        case "medium": return 0.5;
        case "slow": return 1.0;
        case "veryslow": return 2.0;
    }
}


function defineAnimationProps(effect, duration) {

    switch (effect) {

        case "blend":
            return {
                animate: [{opacity: 0}, {opacity: 1}, {opacity: 0}],
                transition: [{ease: "easeInOut", duration: duration}]
            };

        case "shade":
            return {
                animate: [{opacity: 0}, {opacity: 1}, {opacity: 0}],
                transition: [
                    {ease: "easeOut", duration: 0.5 * duration, delay: 0.5 * duration},
                    {ease: "easeIn", duration: 0.5 * duration, delay: 0.0}
                ]
            };

        default:
            return {};
    }
}


/**
 * CBoxView
 *
 * @author Maurizio Tidei, Jens Gruschel
 */
class CBoxView extends React.Component {

    isVisible = null;
    idleTime = new Date();

    state = {
        screensaverRunning: false
    }

    handleEvent(event) {
        switch (event.type) {
            case 'focus':
                this.isVisible = true;
                break;
            case 'blur':
                this.isVisible = false;
                break;
            case 'keydown':
            case 'keyup':
            case 'mousemove':
            case 'mousedown':
            case 'mouseup':
            case 'wheel':
                this.resetIdleTimer();
                break;
            default:
                break;
        }
    }

    componentDidMount() {
        this.aliveTimer = setInterval(() => {this.aliveTick()}, 5000);
        this.idleTimer = setInterval(() => {this.idleTick()}, 1000);
        window.addEventListener('focus', this);
        window.addEventListener('blur', this);
        window.addEventListener('keydown', this);
        window.addEventListener('keyup', this);
        window.addEventListener('mousemove', this);
        window.addEventListener('mousedown', this);
        window.addEventListener('mouseup', this);
        window.addEventListener('wheel', this);
    }

    componentWillUnmount() {
        window.removeEventListener('focus', this);
        window.removeEventListener('blur', this);
        window.removeEventListener('keydown', this);
        window.removeEventListener('keyup', this);
        window.removeEventListener('mousemove', this);
        window.removeEventListener('mousedown', this);
        window.removeEventListener('mouseup', this);
        window.removeEventListener('wheel', this);
        clearInterval(this.idleTimer);
        clearInterval(this.aliveTimer);
    }

    componentDidUpdate(prevProps) {
        if (!this.state.screensaverRunning) {
            if (this.props.idleForce && this.props.idleForce !== prevProps.idleForce) {
                this.setState({screensaverRunning: true})
            }
        }
        else {
            if (this.props.idleTime && this.props.idleTime !== prevProps.idleTime) {
                this.setState({screensaverRunning: false})
            }
        }
    }

    aliveTick() {
        if (this.props.onAlive) this.props.onAlive(this.isVisible, this.props.activityDescription);
    }

    idleTick() {
        if (!this.state.screensaverRunning && this.props.screensaverTimeout) {
            const idleTime = (this.props.idleTime && this.props.idleTime > this.idleTime) ? this.props.idleTime : this.idleTime;
            const milliseconds = new Date() - idleTime;
            if (milliseconds * 0.001 >= this.props.screensaverTimeout) {
                this.setState({screensaverRunning: true});
                // Auto-reset: reload initial CBox state so the next visitor
                // sees a clean menu when they wake the screensaver
                this.props.refreshBox();
            }
        }
    }

    resetIdleTimer() {
        this.idleTime = new Date();
        if (this.state.screensaverRunning) {
            this.setState({screensaverRunning: false});
        }
    }

    render() {

        let shortcutsKeyMap = {};
        let shortcutsHandlers = {};
        for (const i in this.props.shortcuts) {
            shortcutsKeyMap[i] = this.props.shortcuts[i];
            shortcutsHandlers[i] = event => {
                this.props.triggerShortcutAction(i);
            }
        }

        const hotkeys = !_.isEmpty(shortcutsKeyMap) && <GlobalHotKeys keyMap={shortcutsKeyMap} handlers={shortcutsHandlers}/>;

        if (this.state.screensaverRunning && this.props.screensaver && this.props.screensaver.content) {
            return (
                <div className={this.props.classes.screen} style={{backgroundColor: this.props.boxProperties.backgroundColor || '#000000'}}>
                    {this.renderScreensaver()}
                    {hotkeys}
                </div>
            );
        }

        // render content and menus
        // (menu B first, so menu A is in front of menu B naturally without ordering)        
        return <DndProvider backend={MultiBackend} options={HTML5toTouch}>
            <div className={this.props.classes.screen} style={{backgroundColor: this.props.boxProperties.backgroundColor || '#000000'}}>
                {this.props.baseStylesheet && <style>
                    {this.props.baseStylesheet}
                </style>}
                {this.props.menuProperties.stylesheet && <style>
                    {this.props.menuProperties.stylesheet}
                </style>}
                {this.renderBackdrop()}
                {this.renderContent()}
                {this.renderMenu("B", "menu-b", this.props.menus.B)}
                {this.renderMenu("A", "menu-a", this.props.menus.A)}
                {/* Tonfiguren / Stockach frame: scroll buttons + footer — only shown when
                   menu CSS contains TONFIGUREN_CBOX_FRAME or STOCKACH_CBOX_FRAME marker */}
                {this.props.menuProperties.stylesheet &&
                 (this.props.menuProperties.stylesheet.includes('TONFIGUREN_CBOX_FRAME') ||
                  this.props.menuProperties.stylesheet.includes('STOCKACH_CBOX_FRAME')) &&
                <React.Fragment>
                    <div className="cbox-scroll-up" style={{
                        position: 'fixed', right: 28, top: 100, width: 72, height: 72,
                        borderRadius: '50%', background: 'rgba(0,14,20,0.85)',
                        borderTop: '2.5px solid rgba(0,229,204,0.45)',
                        borderRight: '2.5px solid rgba(0,229,204,0.45)',
                        borderBottom: '2.5px solid rgba(0,229,204,0.45)',
                        borderLeft: '2.5px solid rgba(0,229,204,0.45)',
                        cursor: 'pointer', zIndex: 10001
                    }} onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        // Prefer menu-b if it's on-screen (not slid away by Slide transition).
                        // After returning from detail, .objectinfopanel stays mounted with stale
                        // scrollable content — so we can't rely on scrollHeight alone.
                        const menuB = document.getElementById('menu-b');
                        const panel = document.querySelector('.objectinfopanel');
                        const menuBOnScreen = menuB && menuB.getBoundingClientRect().left >= 0;
                        const target = menuBOnScreen ? menuB
                            : (panel && panel.scrollHeight > panel.clientHeight + 5) ? panel : null;
                        if (target) target.scrollBy({top: -400, behavior: 'smooth'});
                    }}>
                        <div style={{position: 'absolute', top: '50%', left: '50%', width: 20, height: 20,
                            borderLeft: '3px solid #00e5cc', borderTop: '3px solid #00e5cc',
                            borderRight: 'none', borderBottom: 'none',
                            transform: 'translate(-50%, -30%) rotate(45deg)', pointerEvents: 'none'}} />
                    </div>
                    <div className="cbox-scroll-down" style={{
                        position: 'fixed', right: 28, bottom: 180, width: 72, height: 72,
                        borderRadius: '50%', background: 'rgba(0,14,20,0.85)',
                        borderTop: '2.5px solid rgba(0,229,204,0.45)',
                        borderRight: '2.5px solid rgba(0,229,204,0.45)',
                        borderBottom: '2.5px solid rgba(0,229,204,0.45)',
                        borderLeft: '2.5px solid rgba(0,229,204,0.45)',
                        cursor: 'pointer', zIndex: 10001
                    }} onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const menuB = document.getElementById('menu-b');
                        const panel = document.querySelector('.objectinfopanel');
                        const menuBOnScreen = menuB && menuB.getBoundingClientRect().left >= 0;
                        const target = menuBOnScreen ? menuB
                            : (panel && panel.scrollHeight > panel.clientHeight + 5) ? panel : null;
                        if (target) target.scrollBy({top: 400, behavior: 'smooth'});
                    }}>
                        <div style={{position: 'absolute', top: '50%', left: '50%', width: 20, height: 20,
                            borderRight: '3px solid #00e5cc', borderBottom: '3px solid #00e5cc',
                            borderLeft: 'none', borderTop: 'none',
                            transform: 'translate(-50%, -70%) rotate(45deg)', pointerEvents: 'none'}} />
                    </div>
                    {/* Footer — powered by + sponsor logos */}
                    <div style={{
                        position: 'fixed', bottom: 0, left: 0, right: 0, height: 130,
                        background: '#0a0e14', zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '0 30px', boxSizing: 'border-box',
                        borderTop: '1px solid rgba(0,229,204,0.06)'
                    }}>
                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6}}>
                            <span style={{fontFamily: "'Roboto', sans-serif", fontSize: 9, letterSpacing: 3,
                                textTransform: 'uppercase', color: '#00e5cc'}}>powered by</span>
                            <img src="/cbox/contexagon_logo_on_dark.png" alt="contexagon"
                                className="ctx-logo-pulse"
                                style={{height: 36}} />
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: 16}}>
                            <span style={{fontFamily: "'Roboto', sans-serif", fontSize: 9, letterSpacing: 3,
                                textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)',
                                writingMode: 'vertical-rl', transform: 'rotate(180deg)'}}>Gefördert durch</span>
                            <div style={{
                                background: 'rgba(255,255,255,0.92)', borderRadius: 10,
                                padding: '10px 20px', display: 'flex', gap: 16, alignItems: 'center'
                            }}>
                                <img src="/cbox/foerder_bkm_bundesregierung.png" alt="Beauftragte der Bundesregierung für Kultur und Medien" style={{height: 50}} />
                                <img src="/cbox/foerder_bkm.png" alt="Neustart Kultur" style={{height: 50}} />
                                <img src="/cbox/foerder_ksl.png" alt="Kulturstiftung der Länder" style={{height: 50}} />
                                <img src="/cbox/foerder_kulturgemeinschaften.png" alt="Kulturgemeinschaften" style={{height: 36}} />
                                <img src="/cbox/foerder_stockach.png" alt="Stadt Stockach" style={{height: 30}} />
                                {this.props.menuProperties.stylesheet.includes('STOCKACH_CBOX_FRAME')
                                    ? <img src="/cbox/fnm_logo.svg" alt="Fasnachtsmuseum Narrenburg" style={{height: 40, filter: 'brightness(0)'}} />
                                    : <img src="/cbox/foerder_stadtmuseum.png" alt="Stadtmuseum" style={{height: 36}} />
                                }
                            </div>
                        </div>
                    </div>
                </React.Fragment>}
                {/* Regal / Bräuche frame: header + footer — no scroll buttons, no sponsors */}
                {this.props.menuProperties.stylesheet &&
                 (this.props.menuProperties.stylesheet.includes('REGAL_CBOX_FRAME') ||
                  this.props.menuProperties.stylesheet.includes('BRAEUCHE_CBOX_FRAME')) &&
                <React.Fragment>
                    {/* Header — museum logo */}
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, height: 64,
                        background: '#0a0e14', zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderBottom: '1px solid rgba(0,229,204,0.10)'
                    }}>
                        <img src="/cbox/fnm_logo.svg" alt="Fasnachtsmuseum" style={{height: 40}} />
                    </div>
                    {/* Footer — pulsating contexagon logo */}
                    <div style={{
                        position: 'fixed', bottom: 0, left: 0, right: 0,
                        background: '#0a0e14', zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '10px 16px', boxSizing: 'border-box',
                        borderTop: '1px solid rgba(0,229,204,0.06)', gap: 8
                    }}>
                        <span style={{fontFamily: "'Roboto', sans-serif", fontSize: 7, letterSpacing: 2,
                            textTransform: 'uppercase', color: '#00e5cc'}}>powered by</span>
                        <img src="/cbox/contexagon_logo_on_dark.png" alt="contexagon"
                            className="ctx-logo-pulse"
                            style={{height: 18}} />
                    </div>
                </React.Fragment>}
                {/* Video screen frame: header + footer + transparent overlay with title & description */}
                {this.props.menuProperties.stylesheet &&
                 this.props.menuProperties.stylesheet.includes('VIDEO_CBOX_FRAME') &&
                <React.Fragment>
                    {/* Header — museum logo */}
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, height: 64,
                        background: '#0a0e14', zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderBottom: '1px solid rgba(0,229,204,0.10)'
                    }}>
                        <img src="/cbox/fnm_logo.svg" alt="Fasnachtsmuseum" style={{height: 40}} />
                    </div>
                    {/* Transparent overlay — video title + description */}
                    {(this.props.playback.content || this.props.boxDescription) &&
                    <div className="video-overlay" style={{
                        position: 'fixed', bottom: 52, left: 0, right: 0, zIndex: 999,
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.85) 30%)',
                        padding: '60px 40px 20px 40px', pointerEvents: 'none'
                    }}>
                        {this.props.playback.content && this.props.playback.content.fields &&
                         this.props.playback.content.fields.title &&
                        <div style={{
                            fontFamily: "'Londrina Solid', cursive",
                            fontSize: 28, color: '#00e5cc', letterSpacing: 2,
                            textTransform: 'uppercase', marginBottom: 8
                        }}>
                            {this.props.playback.content.fields.title}
                        </div>}
                        {this.props.boxDescription &&
                        <div style={{
                            fontFamily: "'Roboto', sans-serif",
                            fontSize: 16, color: 'rgba(255,255,255,0.85)',
                            lineHeight: 1.5, maxWidth: '80%'
                        }}>
                            {this.props.boxDescription}
                        </div>}
                    </div>}
                    {/* Footer — pulsating contexagon logo */}
                    <div style={{
                        position: 'fixed', bottom: 0, left: 0, right: 0,
                        background: '#0a0e14', zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '10px 16px', boxSizing: 'border-box',
                        borderTop: '1px solid rgba(0,229,204,0.06)', gap: 8
                    }}>
                        <span style={{fontFamily: "'Roboto', sans-serif", fontSize: 7, letterSpacing: 2,
                            textTransform: 'uppercase', color: '#00e5cc'}}>powered by</span>
                        <img src="/cbox/contexagon_logo_on_dark.png" alt="contexagon"
                            className="ctx-logo-pulse"
                            style={{height: 18}} />
                    </div>
                </React.Fragment>}
                {hotkeys}
                <Preview>
                    <DraggableMenuObjectPreview className={"menuobject"}/>
                </Preview>
            </div>
        </DndProvider>;
    }

    renderScreensaver() {

        const {classes, screensaver, notification, screenIndex} = this.props;

        return <div id="cbox-background" className={classes.background}>
            <div id="cbox-screensaver" className={classes.screensaver}>
                <ContentView
                    screenIndex={screenIndex}
                    content={screensaver.content}
                    referencedObjects={screensaver.referencedObjects}
                    animationProps={this.props.animationProps}
                    loop={true}
                />
            </div>
            <NotificationBar text={notification} />
        </div>;
    }

    renderBackdrop() {

        const {content} = this.props.backdrop;

        if (content && content.fields && content.type === "mediaitem") {
            return <MediaPanel
                style={{ width: 0, height: 0 }}
                url={content.fields.url}
                restart={this.props.backdrop.restart}
                autoplay={true}
                showControls={false}
                loop={this.props.backdrop.loop}
                volume={this.props.volume}
                otherFilesAsDownload={false}
            />;
        }
        else {
            return null;
        }
    }

    renderContent() {

        const {classes, chatOverlay, notification, screenIndex} = this.props;

        switch (this.props.mode) {

            case CONTENT_MODE:

                const playback = this.props.playback;

                return <div id="cbox-background" className={classes.background}>
                    <div id="cbox-content" className={classes.content}>
                        <ContentView
                            screenIndex={screenIndex}
                            boxName={this.props.boxName}
                            content={playback.content}
                            referencedObjects={{...playback.referencedObjects, ...this.props.menuProperties.menus.B.referencedObjects}}
                            animationProps={this.props.animationProps}
                            navigationIndex={playback.navigationIndex}
                            loop={playback.loop}
                            paused={this.props.paused}
                            volume={this.props.volume}
                            playbackPosition={this.props.playbackPosition}
                            onPlaybackEnded={this.props.onPlaybackEnd}
                            restart={playback.restart}
                        />
                    </div>
                    {chatOverlay && <ChatView overlay={true} />}
                    <NotificationBar text={notification} />
                </div>;

            case CHAT_MODE:

                return <div id="cbox-background" className={classes.background}>
                    <ChatView overlay={false} />
                    <NotificationBar text={notification} />
                </div>;

            default:

                return <div id="cbox-background" className={classes.background}>
                    {chatOverlay && <ChatView overlay={true} />}
                    <NotificationBar text={notification} />
                </div>;
        }
    }

    restrictMenuVisibility(visibility, visible) {
        switch (visibility) {
            case "arbitrary": return visible;
            case "chatonly": return visible && this.props.mode === CHAT_MODE;
            case "contentonly": return visible && this.props.mode === CONTENT_MODE;
            case "mediaonly": return visible && this.props.mode === CONTENT_MODE && this.props.playbackContentType === "mediaitem";
            case "slidesonly": return visible && this.props.mode === CONTENT_MODE && this.props.playbackContentType === "textslide";
            case "htmlonly": return visible && this.props.mode === CONTENT_MODE && this.props.playbackContentType === "htmlcontent";
            default: return visible;
        }
    }

    renderMenu(slot, elementId, menu) {
        if (menu && menu.menu && menu.menu.fields) {
            const visible = this.restrictMenuVisibility(menu.menu.fields.visibility, menu.flags.visible);
            return <MenuFrame
                slot={slot}
                elementId={elementId}
                menu={menu.menu}
                flags={{...menu.flags, visible: visible}}
                referencedObjects={menu.referencedObjects}
            />;
        }
        else {
            return null;
        }
    }
}

const styles = theme => ({
    screen: {
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        justifyContent: "space-between",
        alignItems: "stretch"
    },
    background: {
        position: "relative",
        width: "100vw",
        height: "100vh",
        overflow: "hidden"
    },
    screensaver: {
        position: "sticky", // avoid scrolling with overlay chat
        top: 0,
        height: "100%"
    },
    content: {
        position: "sticky", // avoid scrolling with overlay chat
        top: 0,
        height: "100%"
    }
});

const mapStateToProps = (state, props) => {

    const translate = (value) => selectTranslation(state.configuration, value, state.cbox.language);

    const playback = state.cbox.playback;

    return {
        screenIndex: state.cbox.screenIndex,
        boxProperties: state.cbox.properties || {},
        boxDescription: (state.cbox.properties || {}).description || '',
        boxName: state.cbox.boxName,
        chatProperties: state.cbox.properties.chat || {},
        menuProperties: state.cbox.properties.menu || {},
        baseStylesheet: state.cbox.properties.baseStylesheet || selectBaseStylesheet(state.configuration),
        mode: state.cbox.mode,
        playback: playback,
        playbackContentType: playback && playback.content ? playback.content.type : undefined,
        playbackPosition: state.cbox.playbackPosition,
        paused: !!state.cbox.paused,
        volume: state.cbox.volume,
        chatOverlay: state.cbox.chatOverlay || (state.cbox.properties.chat && state.cbox.properties.chat.display === 'permanent'),
        backdrop: state.cbox.backdrop,
        notification: translate(state.cbox.notification),
        menus: state.cbox.menus || {},
        activityDescription: state.cbox.activity.description,
        screensaver: state.cbox.screensaver,
        screensaverTimeout: state.cbox.screensaver.timeout || 0,
        idleTime: state.cbox.idle.time,
        idleForce: state.cbox.idle.force,
        shortcuts: state.cbox.shortcuts,
        animationProps: defineAnimationProps(
            state.cbox.properties.animation.transitioneffect,
            qunatifyDuration(state.cbox.properties.animation.transitionduration)
        ),
        onPlaybackEnd: () => {
            console.log("playback ended", state.cbox.boxName);
            props.onNotifyEvent('playbackend', state.cbox.boxName, {});
        }
    };
};

export default connect(mapStateToProps, {triggerShortcutAction, refreshBox})(withStyles(styles)(CBoxView));